import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

export const leadsApi = createApi({
  reducerPath: 'leadsApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['Leads'],
  endpoints: (builder) => ({
    addLead: builder.mutation({
      query: (body) => ({
        url: '/leads',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads'],
    }),
    getLeads: builder.query({
      // Build the query string by hand so comma-separated ids (assignedTo=id1,id2)
      // stay literal instead of being percent-encoded to %2C.
      query: (params: Record<string, unknown> = {}) => {
        const parts: string[] = [];
        Object.entries(params).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return;
          const encoded = encodeURIComponent(String(value)).replace(/%2C/gi, ',');
          parts.push(`${key}=${encoded}`);
        });
        const qs = parts.length ? `?${parts.join('&')}` : '';
        return { url: `/leads${qs}`, method: 'GET' };
      },
      providesTags: ['Leads'],
    }),
    getLeadById: builder.query({
      query: (id) => ({
        url: `/leads/${id}`,
        method: 'GET',
      }),
      providesTags: ['Leads'],
    }),
    getTeamDashboard: builder.query({
      query: (params: Record<string, unknown> = {}) => {
        const parts: string[] = [];
        Object.entries(params).forEach(([k, v]) => {
          if (v === undefined || v === null || v === '') return;
          parts.push(`${k}=${encodeURIComponent(String(v))}`);
        });
        const qs = parts.length ? `?${parts.join('&')}` : '';
        return { url: `/leads/team-dashboard${qs}`, method: 'GET' };
      },
      providesTags: ['Leads'],
    }),
    deleteLead: builder.mutation({
      query: (id) => ({
        url: `/leads/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Leads'],
    }),
    updateLead: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/leads/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Leads'],
    }),
    assignLead: builder.mutation({
      query: ({ id, salesPersonId }) => ({
        url: `/leads/${id}/assign`,
        method: 'PATCH',
        body: { salesPersonId },
      }),
      invalidatesTags: ['Leads'],
    }),
    updateLeadGroups: builder.mutation({
      query: ({ id, groups }) => ({
        url: `/leads/${id}/groups`,
        method: 'PATCH',
        body: { groups },
      }),
      invalidatesTags: ['Leads'],
    }),
    updateLeadStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/leads/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['Leads'],
    }),
    updateLeadNotes: builder.mutation({
      query: ({ id, noteId, notes }) => ({
        url: `/leads/${id}/notes`,
        method: 'PATCH',
        body: { noteId, notes },
      }),
      invalidatesTags: ['Leads'],
    }),
    getLeadConvertData: builder.query({
      query: (id) => ({
        url: `/leads/${id}/convert-data`,
        method: 'GET',
      }),
    }),
    convertLead: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/leads/${id}/convert`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads'],
    }),
    getLeadActivities: builder.query({
      query: (id) => ({
        url: `/leads/${id}/activities`,
        method: 'GET',
      }),
      providesTags: ['Leads'],
    }),
    addLeadActivity: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/leads/${id}/activities`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads'],
    }),
  }),
});

export const { useAddLeadMutation, useUpdateLeadMutation, useDeleteLeadMutation, useGetLeadsQuery, useGetLeadByIdQuery, useAssignLeadMutation, useUpdateLeadStatusMutation, useUpdateLeadNotesMutation, useGetLeadConvertDataQuery, useConvertLeadMutation, useGetLeadActivitiesQuery, useAddLeadActivityMutation, useUpdateLeadGroupsMutation, useGetTeamDashboardQuery } = leadsApi;
